import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    if (!token) return toast.error("Reset token missing from URL");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please sign in");
      setTimeout(() => navigate("/login"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" style={{ fontFamily: "Manrope" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-8 h-8 bg-blue-600 rounded text-white flex items-center justify-center">
            <BarChart3 className="w-4 h-4" />
          </div>
          <div className="font-semibold text-slate-900">RSOC × Hub</div>
        </div>

        <h1 className="text-2xl font-semibold mb-2">Set a new password</h1>
        <p className="text-sm text-slate-500 mb-6">Choose a password you haven't used before — at least 8 characters.</p>

        {!token && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700 mb-4" data-testid="missing-token-banner">
            Reset token is missing from the URL. Request a new link from the <Link to="/forgot-password" className="underline">forgot password</Link> page.
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
          <input
            type="password"
            autoFocus
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password (min 8 chars)"
            data-testid="reset-pw1-input"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <input
            type="password"
            required
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="Confirm new password"
            data-testid="reset-pw2-input"
            className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <button
            type="submit"
            disabled={busy || !token}
            data-testid="reset-submit-btn"
            className="w-full bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            {busy ? "Saving…" : "Update password"}
          </button>
          <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-slate-900 mt-3">
            Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}
